import * as i0 from '@angular/core';
import { Directive, Optional, Input, Host, NgModule } from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { convertToRem } from '@appkit4/angular-components/utils';
import * as i1 from '@angular/cdk/layout';
import { LayoutModule } from '@angular/cdk/layout';
import * as i2 from '@angular/cdk/platform';
import { PlatformModule } from '@angular/cdk/platform';
import * as i3 from '@angular/cdk/bidi';
import { BidiModule } from '@angular/cdk/bidi';
import { CommonModule } from '@angular/common';

const gridResponsiveMap = {
    xs: '(max-width: 35.9375rem)', // 575px
    sm: '(min-width: 36rem)', // 576px
    md: '(min-width: 48rem)', // 768px
    lg: '(min-width: 62rem)', // 992px
    xl: '(min-width: 75rem)', // 1200px
    xxl: '(min-width: 100rem)' // 1600px
};
var ApBreakpointEnum;
(function (ApBreakpointEnum) {
    ApBreakpointEnum["xxl"] = "xxl";
    ApBreakpointEnum["xl"] = "xl";
    ApBreakpointEnum["lg"] = "lg";
    ApBreakpointEnum["md"] = "md";
    ApBreakpointEnum["sm"] = "sm";
    ApBreakpointEnum["xs"] = "xs";
})(ApBreakpointEnum || (ApBreakpointEnum = {}));
class ApRowDirective {
    getGutter() {
        const results = [null, null];
        const gutter = this.apGutter || 0;
        const normalizedGutter = Array.isArray(gutter) ? gutter : [gutter, null];
        normalizedGutter.forEach((g, index) => {
            if (typeof g === 'object' && g !== null) {
                results[index] = null;
                Object.keys(gridResponsiveMap).forEach((screen) => {
                    const bp = screen;
                    if (this.mediaMatcher.matchMedia(gridResponsiveMap[bp]).matches && g[bp]) {
                        results[index] = g[bp];
                    }
                });
            }
            else {
                results[index] = Number(g) || null;
            }
        });
        return results;
    }
    setGutterStyle() {
        const [horizontalGutter, verticalGutter] = this.getGutter();
        this.actualGutter$.next([horizontalGutter, verticalGutter]);
        const renderGutter = (name, gutter) => {
            const nativeElement = this.elementRef.nativeElement;
            if (gutter !== null) {
                this.renderer.setStyle(nativeElement, name, `${convertToRem(-1 * gutter / 2)}`);
            }
        };
        renderGutter('margin-left', horizontalGutter);
        renderGutter('margin-right', horizontalGutter);
        renderGutter('margin-top', verticalGutter);
        renderGutter('margin-bottom', verticalGutter);
    }
    constructor(elementRef, renderer, mediaMatcher, ngZone, platform, directionality) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.mediaMatcher = mediaMatcher;
        this.ngZone = ngZone;
        this.platform = platform;
        this.directionality = directionality;
        this.apAlign = null;
        this.apJustify = null;
        this.apGutter = null;
        this.actualGutter$ = new ReplaySubject(1);
        this.dir = 'ltr';
        this.destroy$ = new Subject();
        // TODO: move to host after View Engine deprecation
        this.elementRef.nativeElement.classList.add('ap-row');
    }
    ngOnInit() {
        this.dir = this.directionality.value;
        this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction) => {
            this.dir = direction;
        });
        this.setGutterStyle();
    }
    ngOnChanges(changes) {
        if (changes.apGutter) {
            this.setGutterStyle();
        }
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ApRowDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1.MediaMatcher }, { token: i0.NgZone }, { token: i2.Platform }, { token: i3.Directionality, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.11", type: ApRowDirective, selector: "[ap-row],ap-row,ap-form-item", inputs: { apAlign: "apAlign", apJustify: "apJustify", apGutter: "apGutter" }, host: { properties: { "class.ap-row-top": "apAlign === 'top'", "class.ap-row-middle": "apAlign === 'middle'", "class.ap-row-bottom": "apAlign === 'bottom'", "class.ap-row-start": "apJustify === 'start'", "class.ap-row-end": "apJustify === 'end'", "class.ap-row-center": "apJustify === 'center'", "class.ap-row-space-around": "apJustify === 'space-around'", "class.ap-row-space-between": "apJustify === 'space-between'", "class.ap-row-rtl": "dir === \"rtl\"" } }, exportAs: ["apRow"], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ApRowDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ap-row],ap-row,ap-form-item',
                    exportAs: 'apRow',
                    host: {
                        '[class.ap-row-top]': `apAlign === 'top'`,
                        '[class.ap-row-middle]': `apAlign === 'middle'`,
                        '[class.ap-row-bottom]': `apAlign === 'bottom'`,
                        '[class.ap-row-start]': `apJustify === 'start'`,
                        '[class.ap-row-end]': `apJustify === 'end'`,
                        '[class.ap-row-center]': `apJustify === 'center'`,
                        '[class.ap-row-space-around]': `apJustify === 'space-around'`,
                        '[class.ap-row-space-between]': `apJustify === 'space-between'`,
                        '[class.ap-row-rtl]': `dir === "rtl"`
                    },
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1.MediaMatcher }, { type: i0.NgZone }, { type: i2.Platform }, { type: i3.Directionality, decorators: [{
                    type: Optional
                }] }], propDecorators: { apAlign: [{
                type: Input
            }], apJustify: [{
                type: Input
            }], apGutter: [{
                type: Input
            }] } });

function isNotNil(value) {
    return typeof value !== 'undefined' && value !== null;
}
class ApColDirective {
    setHostClassMap() {
        const hostClassMap = {
            ['ap-col']: true,
            [`ap-col-${this.apSpan}`]: isNotNil(this.apSpan),
            [`ap-col-order-${this.apOrder}`]: isNotNil(this.apOrder),
            [`ap-col-offset-${this.apOffset}`]: isNotNil(this.apOffset),
            [`ap-col-pull-${this.apPull}`]: isNotNil(this.apPull),
            [`ap-col-push-${this.apPush}`]: isNotNil(this.apPush),
            ['ap-col-rtl']: this.dir === 'rtl',
            ...this.generateClass()
        };
        for (const i in this.classMap) {
            if (this.classMap.hasOwnProperty(i)) {
                this.renderer.removeClass(this.elementRef.nativeElement, i);
            }
        }
        this.classMap = { ...hostClassMap };
        for (const i in this.classMap) {
            if (this.classMap.hasOwnProperty(i) && this.classMap[i]) {
                this.renderer.addClass(this.elementRef.nativeElement, i);
            }
        }
    }
    setHostFlexStyle() {
        this.hostFlexStyle = this.parseFlex(this.apFlex);
    }
    parseFlex(flex) {
        if (typeof flex === 'number') {
            return `${flex} ${flex} auto`;
        }
        else if (typeof flex === 'string') {
            if (/^\d+(\.\d+)?(px|em|rem|%)$/.test(flex)) {
                return `0 0 ${flex}`;
            }
        }
        return flex;
    }
    generateClass() {
        const listOfSizeInputName = ['apXs', 'apSm', 'apMd', 'apLg', 'apXl', 'apXXl'];
        const listClassMap = {};
        listOfSizeInputName.forEach(name => {
            const sizeName = name.replace('ap', '').toLowerCase();
            if (isNotNil(this[name])) {
                if (typeof this[name] === 'number' || typeof this[name] === 'string') {
                    listClassMap[`ap-col-${sizeName}-${this[name]}`] = true;
                }
                else {
                    const embedded = this[name];
                    const prefixArray = ['span', 'pull', 'push', 'offset', 'order'];
                    prefixArray.forEach(prefix => {
                        const prefixClass = prefix === 'span' ? '-' : `-${prefix}-`;
                        listClassMap[`ap-col-${sizeName}${prefixClass}${embedded[prefix]}`] = embedded && isNotNil(embedded[prefix]);
                    });
                }
            }
        });
        return listClassMap;
    }
    constructor(elementRef, apRowDirective, renderer, directionality) {
        this.elementRef = elementRef;
        this.apRowDirective = apRowDirective;
        this.renderer = renderer;
        this.directionality = directionality;
        this.classMap = {};
        this.destroy$ = new Subject();
        this.hostFlexStyle = null;
        this.dir = 'ltr';
        this.apFlex = null;
        this.apSpan = null;
        this.apOrder = null;
        this.apOffset = null;
        this.apPush = null;
        this.apPull = null;
        this.apXs = null;
        this.apSm = null;
        this.apMd = null;
        this.apLg = null;
        this.apXl = null;
        this.apXXl = null;
    }
    ngOnInit() {
        this.dir = this.directionality.value;
        this.directionality.change.pipe(takeUntil(this.destroy$)).subscribe((direction) => {
            this.dir = direction;
            this.setHostClassMap();
        });
        this.setHostClassMap();
        this.setHostFlexStyle();
    }
    ngOnChanges(changes) {
        this.setHostClassMap();
        const { apFlex } = changes;
        if (apFlex) {
            this.setHostFlexStyle();
        }
    }
    ngAfterViewInit() {
        if (this.apRowDirective) {
            this.apRowDirective.actualGutter$.pipe(takeUntil(this.destroy$)).subscribe(([horizontalGutter, verticalGutter]) => {
                const renderGutter = (name, gutter) => {
                    const nativeElement = this.elementRef.nativeElement;
                    if (gutter !== null) {
                        this.renderer.setStyle(nativeElement, name, `${gutter / 2}px`);
                    }
                };
                renderGutter('padding-left', horizontalGutter);
                renderGutter('padding-right', horizontalGutter);
                renderGutter('padding-top', verticalGutter);
                renderGutter('padding-bottom', verticalGutter);
            });
        }
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ApColDirective, deps: [{ token: i0.ElementRef }, { token: ApRowDirective, host: true, optional: true }, { token: i0.Renderer2 }, { token: i3.Directionality, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.11", type: ApColDirective, selector: "[ap-col],ap-col,ap-form-control,ap-form-label", inputs: { apFlex: "apFlex", apSpan: "apSpan", apOrder: "apOrder", apOffset: "apOffset", apPush: "apPush", apPull: "apPull", apXs: "apXs", apSm: "apSm", apMd: "apMd", apLg: "apLg", apXl: "apXl", apXXl: "apXXl" }, host: { properties: { "style.flex": "hostFlexStyle" } }, exportAs: ["apCol"], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: ApColDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ap-col],ap-col,ap-form-control,ap-form-label',
                    exportAs: 'apCol',
                    host: {
                        '[style.flex]': 'hostFlexStyle'
                    }
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: ApRowDirective, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }] }, { type: i0.Renderer2 }, { type: i3.Directionality, decorators: [{
                    type: Optional
                }] }], propDecorators: { apFlex: [{
                type: Input
            }], apSpan: [{
                type: Input
            }], apOrder: [{
                type: Input
            }], apOffset: [{
                type: Input
            }], apPush: [{
                type: Input
            }], apPull: [{
                type: Input
            }], apXs: [{
                type: Input
            }], apSm: [{
                type: Input
            }], apMd: [{
                type: Input
            }], apLg: [{
                type: Input
            }], apXl: [{
                type: Input
            }], apXXl: [{
                type: Input
            }] } });

class GridModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: GridModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.11", ngImport: i0, type: GridModule, declarations: [ApColDirective, ApRowDirective], imports: [BidiModule, CommonModule, LayoutModule, PlatformModule], exports: [ApColDirective, ApRowDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: GridModule, imports: [BidiModule, CommonModule, LayoutModule, PlatformModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: GridModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ApColDirective, ApRowDirective],
                    exports: [ApColDirective, ApRowDirective],
                    imports: [BidiModule, CommonModule, LayoutModule, PlatformModule]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ApColDirective, ApRowDirective, GridModule };
